import type { MouseEvent, ReactNode } from 'react';

import { useNavigate } from '@/hooks/utils/useNavigate';

interface AppLinkProps {
    /** Where the link leads, and its real `href`: it can be opened in a new tab, copied, read aloud. */
    to: string;
    /** Carried to the destination as the router's location state, as a click would carry it. */
    state?: unknown;
    children: ReactNode;
    /** The link's whole appearance, focus ring included: this component only owns the click. */
    className?: string;
    /** Replaces the visible text as the link's name, when that text alone would say too little. */
    ariaLabel?: string;
    /** Opens a new tab and leaves the click to the browser, so the current page is never left. */
    openInNewTab?: boolean;
    /** Runs before the navigation, e.g. to stop the click from reaching a clickable tile around it. */
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    dataTestId?: string;
}

/** A click the browser should keep for itself: a new tab, a new window, a download. */
const isModifiedClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
    event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

/**
 * Every internal link of the module.
 *
 * A react-router `Link` cannot be used inside the host shell: it moves the module's own history,
 * which leaves the host lighting up the menu entry of the page we just left. This renders the same
 * real `a`, and routes the plain click through `useNavigate`, which hands the navigation to the
 * host. A modified click, and a link that opens a new tab, are left to the browser untouched.
 */
export default function AppLink({
    to,
    state,
    children,
    className,
    ariaLabel,
    openInNewTab,
    onClick,
    dataTestId,
}: AppLinkProps) {
    const navigate = useNavigate();

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);

        if (openInNewTab || isModifiedClick(event)) {
            return;
        }

        event.preventDefault();
        navigate({ path: to, state });
    };

    return (
        <a
            href={to}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            onClick={handleClick}
            className={className}
            aria-label={ariaLabel}
            data-testid={dataTestId}
        >
            {children}
        </a>
    );
}
